import { RadioButtonWithLabelOption, TabStyles, TextLinkWidget } from '@tableau/widgets';
import { InputAttrs } from '@tableau/widgets/lib/src/utils/HtmlAttrs';
import * as React from 'react';
import { style } from 'typestyle';

import { FormikRadioButtonGroup } from '~commonInfra/components/FormikRadioButtonGroup';
import { HelpService } from '~commonInfra/services/HelpService';

import { Messages } from '../../../../../codegen/Localize';
import { FeatureFlags } from '../../../../../ts/FeatureFlags';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useFormikContext } from '../../../../FormikWrapperForStubbing';
import { learnMoreStyle, settingsRadioButtonItemHeight } from '../../../../styles/CommonStyles';
import { UserVisibilityMode } from '../../../../types/ISiteSettingsForSiteAdmin';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export const USER_VISIBILITY_LEARN_MORE_TEST_ID = 'user-visibility-learn-more';
export type UserVisibilitySectionProps = SiteSettingsForSiteAdminProps<'userVisibility'>;
export type UserVisibilitySectionInternalProps = { helpService: HelpService } & UserVisibilitySectionProps;

function getButtons(): RadioButtonWithLabelOption<string>[] {
  return [{
    testId: 'userVisibility-full-radio-button',
    label: Messages.userVisibilityFullLabel(),
    value: 'FULL'
  }, {
    testId: 'userVisibility-limited-radio-button',
    label: Messages.userVisibilityLimitedLabel(),
    value: 'LIMITED'
  }];
}

function getDescription(userVisibilityHelpUrl: string) {
  return <>
    {Messages.userVisibilityMsg()}
    <TextLinkWidget
      className={learnMoreStyle}
      href={userVisibilityHelpUrl}
      target='_blank'
      testId={USER_VISIBILITY_LEARN_MORE_TEST_ID}
    >
      {Messages.learnMore()}
    </TextLinkWidget>
  </>;
}

const warningStyle = style({
  marginTop: TabStyles.Spacing.MediumSpace,
  paddingLeft: TabStyles.Spacing.SmallSpace,
  fontStyle: TabStyles.Colors.AttentionLight,
  color: '#c41723'
});

export function UserVisibilitySectionInternal(props: UserVisibilitySectionInternalProps) {
  const userVisibilityHelpUrl = props.helpService.getServerHelpUrlByCategory('user-visibility');
  const buttonList = React.useMemo(() => getButtons(), []);
  const formik = useFormikContext<UserVisibilitySectionProps>();

  return !FeatureFlags.userVisibilityEnabled ? null : <SettingsSection title={Messages.userVisibility()} description={getDescription(userVisibilityHelpUrl)}>
    <FormikRadioButtonGroup
      name={'settings.userVisibility'}
      buttonList={buttonList}
      testId={'user-visibility-options'}
      className={settingsRadioButtonItemHeight}
      ariaLabel={Messages.userVisibility()}
    />
  </SettingsSection>;
}

/**
 * A wrapper component which provides the dependencies to the internal component
 * @param props
 * @constructor
 */
export function UserVisibilitySection(props: UserVisibilitySectionProps) {
  const deps: UserVisibilitySectionInternalProps = {
    helpService: HelpService,
    settings: props.settings,
    siteId: props.siteId
  };
  return <UserVisibilitySectionInternal {...deps} />;
}

/* for registering the component in settings registry */
export const UserVisibilitySpecs = componentRegistryEntry({
  name: 'UserVisibilitySection',
  component: UserVisibilitySection
});

/* for other components to conditionally render based on this setting */
export function useUserVisibilityLimited() {
  const formik = useFormikContext<UserVisibilitySectionProps>();
  return FeatureFlags.userVisibilityEnabled && formik.initialValues.settings.userVisibility === UserVisibilityMode.LIMITED;
}

/**
 * Forces checkboxes to be unselected when userVisibility is limited.
 * Passing the value in to prevent formik rules from being broken.
 * Intentional design by the SLAP team.
 */
export function forceUncheckedIfUserVisibilityLimited(isUserVisibilityLimited: boolean): Partial<InputAttrs> {
  return {
    checked: isUserVisibilityLimited ? false: undefined
  };
}

